import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, X } from "lucide-react";

const FREE_SHIP = 3500;

export default function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, drawerOpen, setDrawerOpen } = useCart();
  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-surface flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="font-serif text-2xl text-left">Your Bag</SheetTitle>
          <div className="text-xs text-text-secondary text-left">{items.length} {items.length === 1 ? "edition" : "editions"}</div>
        </SheetHeader>

        {/* Free shipping bar */}
        <div className="px-6 py-4 border-b border-border" data-testid="cart-progress-bar">
          {remaining > 0 ? (
            <div className="text-xs text-text-secondary">
              <span className="font-serif text-foreground">₹{remaining.toLocaleString("en-IN")}</span> away from complimentary shipping
            </div>
          ) : (
            <div className="text-xs text-brand-accent ie-overline">Complimentary shipping unlocked</div>
          )}
          <div className="mt-2 h-px bg-border relative">
            <div className="absolute inset-y-0 left-0 bg-brand-accent" style={{ width: `${pct}%`, height: 1 }} />
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="p-10 text-center">
              <div className="ie-overline text-text-secondary mb-4">Your bag is quietly empty</div>
              <Link to="/shop" onClick={() => setDrawerOpen(false)} className="ie-btn-primary inline-flex">Browse the Atelier</Link>
            </div>
          ) : (
            <ul>
              {items.map((i) => (
                <li key={i.sku} className="flex gap-4 p-6 border-b border-border" data-testid={`cart-item-${i.sku}`}>
                  <Link to={`/product/${i.id}`} onClick={() => setDrawerOpen(false)} className="w-20 h-24 bg-background block flex-shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover"/>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="font-serif text-base leading-tight truncate">{i.name}</h4>
                      <button onClick={() => removeItem(i.sku)} aria-label="remove" data-testid={`cart-remove-${i.sku}`}>
                        <X className="w-4 h-4 text-text-secondary hover:text-foreground"/>
                      </button>
                    </div>
                    <div className="ie-overline text-text-secondary mt-1">Size {i.size}</div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border border-border">
                        <button onClick={() => updateQty(i.sku, i.qty - 1)} className="px-2.5 py-1.5 hover:bg-muted" aria-label="decrease" data-testid={`cart-qty-decrease-${i.sku}`}><Minus className="w-3 h-3"/></button>
                        <span className="px-3 text-sm tabular-nums" data-testid={`cart-qty-${i.sku}`}>{i.qty}</span>
                        <button onClick={() => updateQty(i.sku, i.qty + 1)} className="px-2.5 py-1.5 hover:bg-muted" aria-label="increase" data-testid={`cart-qty-increase-${i.sku}`}><Plus className="w-3 h-3"/></button>
                      </div>
                      <span className="font-serif text-base">₹{(i.price * i.qty).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="ie-overline">Subtotal</span>
              <span className="font-serif text-2xl" data-testid="cart-subtotal">₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="text-[11px] text-text-secondary">Taxes & shipping calculated at checkout.</div>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/cart" onClick={() => setDrawerOpen(false)} className="ie-btn-ghost w-full justify-center" data-testid="cart-view-bag">View Bag</Link>
              <Link to="/checkout" onClick={() => setDrawerOpen(false)} className="ie-btn-primary w-full justify-center" data-testid="cart-checkout">Checkout</Link>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
